import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

// One layer of a nested-modal flow (e.g. the Send Fax create-recipient wizard).
// A layer owns only its content and how it labels itself; the stack owns the
// order, the back/breadcrumb chrome, and which layer is visible.
export interface ModalStackLayer {
  // Stable identity for this layer instance. Also its React key, so pushing the
  // same conceptual step twice (nested org create under two contacts) needs
  // distinct keys.
  key: string
  // Short label for the breadcrumb trail (e.g. "Add contact").
  breadcrumb: string
  // Header title for this layer (e.g. "Add new contact").
  title: string
  // Optional header subtitle under the title.
  subtitle?: string
  // Rendered as the layer's body. Called on every render (not stored as an
  // element) so the content stays fresh; a hidden-but-mounted layer keeps its
  // own state while a deeper layer is on top.
  render: () => ReactNode
}

export interface ModalStackHandle {
  // Add a layer on top of the current one.
  push: (layer: ModalStackLayer) => void
  // Swap the top layer, leaving the trail below it untouched. Used when a step
  // shouldn't persist as its own breadcrumb (e.g. the recipient-type chooser
  // becomes "Add contact" once a type is picked).
  replace: (layer: ModalStackLayer) => void
  // Drop the top layer.
  pop: () => void
  // Trim the stack back to (and including) the layer at `index`; index < 0
  // clears every layer and returns to the base content.
  popTo: (index: number) => void
  // Remove every layer, returning to the base content.
  clear: () => void
}

// Null when no ancestor provides a stack: a form rendered standalone (its own
// Dialog) falls back to its internal step handling instead of pushing layers.
export const ModalStackContext = createContext<ModalStackHandle | null>(null)

export function useOptionalModalStack(): ModalStackHandle | null {
  return useContext(ModalStackContext)
}

interface UseModalStackStateResult {
  layers: ModalStackLayer[]
  handle: ModalStackHandle
}

// Owns the layer array and exposes a stable handle. The owner (the outer modal)
// renders the header/body from `layers` and provides `handle` to descendants
// via ModalStackContext.
export function useModalStackState(): UseModalStackStateResult {
  const [layers, setLayers] = useState<ModalStackLayer[]>([])
  const handle = useMemo<ModalStackHandle>(
    () => ({
      push: (layer) => setLayers((current) => [...current, layer]),
      // On an empty stack this behaves like push (no top to drop); callers only
      // replace a layer they just pushed, so that case doesn't arise.
      replace: (layer) =>
        setLayers((current) => [...current.slice(0, -1), layer]),
      pop: () => setLayers((current) => current.slice(0, -1)),
      // index < 0 clears to the base; an index past the end is a no-op slice.
      popTo: (index) =>
        setLayers((current) => (index < 0 ? [] : current.slice(0, index + 1))),
      clear: () => setLayers([]),
    }),
    []
  )
  return { layers, handle }
}
